import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, FileText, ShieldCheck, Loader2, Pencil, Type, Eraser, Download } from "lucide-react";
import { API } from "../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const eur = (n) => `${(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

export default function PublicQuote() {
  const { token } = useParams();
  const [q, setQ] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState("draw");
  const [signerName, setSignerName] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [hasDrawing, setHasDrawing] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  useEffect(() => {
    axios.get(`${API}/public/quotes/${token}`)
      .then((r) => { setQ(r.data); if (r.data.status === "accepted") setDone(true); if (r.data.client?.name) setSignerName(r.data.client.name); })
      .catch(() => setError("Devis introuvable ou lien expiré."))
      .finally(() => setLoading(false));
  }, [token]);

  const pos = (e) => {
    const c = canvasRef.current; const rect = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - rect.left) * (c.width / rect.width), y: (t.clientY - rect.top) * (c.height / rect.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.strokeStyle = "#111"; ctx.lineTo(p.x, p.y); ctx.stroke(); setHasDrawing(true); };
  const end = () => { drawing.current = false; };
  const clearCanvas = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); setHasDrawing(false); };

  const canSign = signerName.trim() && (mode === "draw" ? hasDrawing : accepted);

  const sign = async () => {
    if (!canSign) return;
    setSubmitting(true);
    try {
      const signature = mode === "draw" ? canvasRef.current.toDataURL("image/png") : null;
      await axios.post(`${API}/public/quotes/${token}/sign`, { signature, signer_name: signerName.trim() });
      setDone(true);
    } catch { setError("Une erreur est survenue lors de la signature."); }
    finally { setSubmitting(false); }
  };

  if (loading) return <Center><Loader2 className="animate-spin text-zinc-400" size={32} /></Center>;
  if (error) return <Center><p className="text-signal font-medium">{error}</p></Center>;

  const t = q.totals || {};

  return (
    <div className="min-h-[100dvh] bg-background py-6 px-4">
      <div className="w-full max-w-2xl mx-auto bg-card rounded-2xl border border-white/10 shadow-sm overflow-hidden animate-fade-in" data-testid="public-quote">
        <div className="bg-klein text-zinc-950 p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-md bg-card flex items-center justify-center text-zinc-100 font-cabinet font-black">F</div>
            <span className="font-cabinet font-black text-xl">{q.company_name || "FlowDesk"}</span>
          </div>
          <p className="font-mono-label text-[11px] uppercase tracking-[0.25em] text-white/50 mb-1">Devis · {q.number}</p>
          <h1 className="font-cabinet font-bold text-2xl tracking-tight">{eur(t.total_ttc)} TTC</h1>
          {q.valid_until && <p className="text-zinc-950/60 text-sm mt-1">Valable jusqu'au {q.valid_until}</p>}
        </div>

        <div className="p-6 space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400">Adressé à</p>
              <p className="text-sm font-medium mt-0.5">{q.client?.name || "—"}</p>
              <p className="text-xs text-zinc-400">{q.client?.email}</p>
            </div>
            <a href={`${BACKEND}/api/public/quotes/${token}/pdf`} target="_blank" rel="noreferrer" data-testid="public-quote-pdf" className="flex items-center gap-1.5 text-sm h-9 px-3 rounded-lg border border-white/10 hover:bg-white/5"><Download size={15} /> PDF</a>
          </div>

          <div className="rounded-xl border border-white/10 overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-zinc-400 text-xs border-b border-white/10 bg-white/[0.02]"><th className="px-3 py-2">Description</th><th className="px-3 py-2 text-right">Qté</th><th className="px-3 py-2 text-right">PU HT</th><th className="px-3 py-2 text-right">Total HT</th></tr></thead>
              <tbody>
                {(q.lines || []).map((l, i) => (
                  <tr key={i} className="border-b border-white/[0.06]">
                    <td className="px-3 py-2">{l.description}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{l.qty}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{eur(l.unit_price)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{eur((parseFloat(l.qty) || 0) * (parseFloat(l.unit_price) || 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="space-y-1 text-sm">
            {t.discount > 0 && <Row label="Remise" val={`-${eur(t.discount)}`} muted />}
            <Row label="Total HT" val={eur(t.total_ht)} />
            <Row label="TVA" val={eur(t.total_vat)} muted />
            <div className="flex justify-between font-cabinet font-bold text-lg pt-2 border-t border-white/10 text-klein"><span>Total TTC</span><span className="tabular-nums">{eur(t.total_ttc)}</span></div>
            {q.deposit_percent > 0 && <Row label={`Acompte (${q.deposit_percent}%)`} val={eur(t.total_ttc * q.deposit_percent / 100)} muted />}
          </div>

          {q.notes && <p className="text-zinc-400 text-sm whitespace-pre-wrap border-t border-white/10 pt-4">{q.notes}</p>}

          {done ? (
            <div className="flex flex-col items-center gap-2 py-8 text-emerald-500" data-testid="public-quote-signed">
              <CheckCircle2 size={56} strokeWidth={1.5} />
              <p className="font-cabinet font-bold text-xl">Devis accepté et signé</p>
              <p className="text-sm text-zinc-400">Merci ! Le prestataire a été notifié.</p>
            </div>
          ) : (
            <div className="border-t border-white/10 pt-5 space-y-4">
              <p className="font-cabinet font-bold">Signer le devis</p>
              <div className="flex gap-2">
                <button data-testid="sign-mode-draw" onClick={() => setMode("draw")} className={`flex-1 flex items-center justify-center gap-1.5 h-9 rounded-lg text-sm font-medium border ${mode === "draw" ? "border-klein bg-klein/10 text-klein" : "border-white/10 text-zinc-400"}`}><Pencil size={14} /> Dessiner</button>
                <button data-testid="sign-mode-type" onClick={() => setMode("type")} className={`flex-1 flex items-center justify-center gap-1.5 h-9 rounded-lg text-sm font-medium border ${mode === "type" ? "border-klein bg-klein/10 text-klein" : "border-white/10 text-zinc-400"}`}><Type size={14} /> Valider par nom</button>
              </div>

              <input data-testid="signer-name" value={signerName} onChange={(e) => setSignerName(e.target.value)} placeholder="Votre nom complet" className="w-full border border-white/10 rounded-lg px-3 py-2.5 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />

              {mode === "draw" ? (
                <div>
                  <div className="relative rounded-lg border border-white/15 bg-white overflow-hidden">
                    <canvas ref={canvasRef} width={560} height={180} data-testid="signature-canvas"
                      onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                      onTouchStart={start} onTouchMove={move} onTouchEnd={end}
                      className="w-full touch-none cursor-crosshair" style={{ height: 180 }} />
                    {!hasDrawing && <span className="absolute inset-0 flex items-center justify-center text-zinc-400 text-sm pointer-events-none">Signez ici avec la souris ou le doigt</span>}
                  </div>
                  <button onClick={clearCanvas} className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white mt-2" data-testid="signature-clear"><Eraser size={13} /> Effacer</button>
                </div>
              ) : (
                <label className="flex items-start gap-2.5 text-sm cursor-pointer">
                  <input type="checkbox" data-testid="accept-checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-0.5 w-4 h-4 accent-klein" />
                  <span className="text-zinc-300">J'accepte ce devis ({q.number}) d'un montant de <b>{eur(t.total_ttc)} TTC</b> et confirme mon engagement.</span>
                </label>
              )}

              <button data-testid="public-quote-sign" disabled={!canSign || submitting} onClick={sign} className="w-full flex items-center justify-center gap-2 py-3 rounded-lg bg-emerald-500 text-zinc-950 font-semibold hover:bg-emerald-600 disabled:opacity-50">
                {submitting ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />} Accepter et signer
              </button>
              <p className="flex items-center justify-center gap-1.5 text-xs text-zinc-400"><ShieldCheck size={13} /> Signature sécurisée via FlowDesk</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, val, muted }) {
  return <div className={`flex justify-between ${muted ? "text-zinc-400" : ""}`}><span>{label}</span><span className="tabular-nums">{val}</span></div>;
}

function Center({ children }) {
  return <div className="min-h-[100dvh] flex items-center justify-center bg-background">{children}</div>;
}
